import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  FileText,
  Settings,
  TrendingUp,
  Layers,
  Users
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectTemplateMasterService from '../../services/projectTemplateMasterService'
import RevisionHistoryModal from '../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ProjectTemplateMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalTemplates: 0,
    activeTemplates: 0,
    inactiveTemplates: 0,
    webDevelopmentTemplates: 0,
    mobileDevelopmentTemplates: 0,
    apiDevelopmentTemplates: 0,
    dataScienceTemplates: 0,
    standardTemplates: 0,
    customTemplates: 0,
    frameworkTemplates: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedProjectTemplateData, setSelectedProjectTemplateData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [projectTemplatesResponse, statsResponse] = await Promise.all([
        projectTemplateMasterService.getAllProjectTemplateMasters(),
        projectTemplateMasterService.getProjectTemplateMasterStats()
      ])
      
      if (projectTemplatesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (projectTemplatesResponse.data || []).map(template => ({
          ...template,
          id: template._id,
          formattedTechnologies: template.supportedTechnologies?.map(tech => tech.technologyName).join(', ') || 'Not Set',
          formattedPhases: template.templateStructure?.phases?.length || 0,
          formattedTasks: template.templateStructure?.tasks?.length || 0,
          formattedEstimatedHours: template.resourceRequirements?.estimatedTotalHours || 0,
          currentVersion: `Rev ${template.revisionHistory?.length + 1 || 1}`
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (projectTemplatesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No Project Template Master records found. Create your first Project Template!')
        }
      } else {
        toast.error(projectTemplatesResponse.message || 'Failed to load Project Template Master records')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading Project Template Master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view Project Template Master records')
      } else {
        toast.error('Failed to load Project Template Master records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      key: 'templateCode',
      label: 'Template Code',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <FileText size={14} className="text-blue-600" />
          </div>
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'templateName',
      label: 'Template Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.templateDescription && (
            <div className="text-sm text-gray-500">{row.templateDescription}</div>
          )}
        </div>
      )
    },
    {
      key: 'templateCategory',
      label: 'Category',
      sortable: true,
      render: (value) => {
        const categoryColors = {
          'Web Development': 'bg-blue-100 text-blue-800',
          'Mobile Development': 'bg-green-100 text-green-800',
          'API Development': 'bg-purple-100 text-purple-800',
          'Data Science': 'bg-yellow-100 text-yellow-800',
          'DevOps': 'bg-red-100 text-red-800',
          'Desktop Application': 'bg-indigo-100 text-indigo-800',
          'AI/ML': 'bg-pink-100 text-pink-800',
          'Cloud Migration': 'bg-gray-100 text-gray-800',
          'Legacy Modernization': 'bg-gray-100 text-gray-800',
          'Support': 'bg-gray-100 text-gray-800',
          'Maintenance': 'bg-gray-100 text-gray-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'templateType',
      label: 'Type',
      sortable: true,
      render: (value) => {
        const typeColors = {
          'Standard': 'bg-green-100 text-green-800',
          'Custom': 'bg-blue-100 text-blue-800',
          'Framework': 'bg-purple-100 text-purple-800',
          'Library': 'bg-yellow-100 text-yellow-800',
          'Integration': 'bg-red-100 text-red-800',
          'Migration': 'bg-gray-100 text-gray-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'formattedEstimatedHours',
      label: 'Est. Hours',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-gray-600">
          {value}h
        </div>
      )
    },
    {
      key: 'templateStatus',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'Active' ? 'bg-green-100 text-green-800' : 
          value === 'Inactive' ? 'bg-red-100 text-red-800' :
          value === 'Deprecated' ? 'bg-yellow-100 text-yellow-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {value}
        </span>
      )
    }
  ]

  const handleAddNew = () => {
    navigate('/projects/project-template-master/new')
  }

  const handleEdit = (row) => {
    navigate('/projects/project-template-master/edit', { 
      state: { projectTemplateData: row } 
    })
  }

  const handleView = (row) => {
    navigate(`/projects/project-template-master/view/${row.id}`)
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await projectTemplateMasterService.deleteProjectTemplateMaster(deleteItem.id)
      if (response.success) {
        toast.success('Project Template Master deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete Project Template Master')
      }
    } catch (error) {
      console.error('Error deleting Project Template Master:', error)
      toast.error('Failed to delete Project Template Master')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (row) => {
    setSelectedProjectTemplateData(row)
    setShowRevisionHistory(true)
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Projects',
      path: '/projects',
      onClick: () => navigate('/projects')
    },
    {
      label: 'Masters',
      path: '/projects?tab=masters',
      onClick: () => navigate('/projects?tab=masters')
    },
    {
      label: 'Project Template Master',
      path: '/projects/project-template-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project Template Master</h1>
          <p>Manage project templates and their specifications for your organization</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalTemplates}</div>
          <div className="metric-title">Total Templates</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Settings size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeTemplates}</div>
          <div className="metric-title">Active Templates</div>
          <div className="metric-subtitle">Currently available</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Layers size={16} />
            </div>
            <div className="metric-trend neutral">
              <TrendingUp size={12} />
              <span>Stable</span>
            </div>
          </div>
          <div className="metric-value">{stats.webDevelopmentTemplates}</div>
          <div className="metric-title">Web Templates</div>
          <div className="metric-subtitle">Web development</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.standardTemplates}</div>
          <div className="metric-title">Standard Templates</div>
          <div className="metric-subtitle">Standard project types</div>
        </div>
      </div>

      {/* Project Template Master Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search project templates..."
          emptyMessage="No project templates found. Create your first project template!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Project Template Master"
        message={`Are you sure you want to delete "${deleteItem?.templateName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Revision History Modal */}
      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        data={selectedProjectTemplateData}
        title="Project Template Master Revision History"
      />
    </div>
  )
}

export default ProjectTemplateMaster
